import { FunctionComponent, useCallback } from "react";
import { ExternalIcon } from "@icons/ExternalIcon";
import {
  selectOverlayScreen,
  setOverlayScreenAction,
} from "@store/app/appReducer";
import { LinkButton } from "@ui-kit/LinkButton/LinkButton";
import { useAppDispatch, useAppSelector } from "@store/store";
import { Text } from "@ui-kit/Text/Text";
import { useTranslation } from "react-i18next";
import { addStartPoint } from "@store/plan/planReducer";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { AddressAutoComplete } from "@modules/plan/AddressAutoComplete/AddressAutoComplete";
import { useUserLocation } from "@hooks/useUserLocation/useUserLocation";

import "./StartingPoint.css";

const StartingPoint: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const overlayScreen = useAppSelector(selectOverlayScreen);
  const { getUserLocation } = useUserLocation();
  const dayIndex = (overlayScreen?.params?.dayIndex ?? null) as number | null;

  const handleAutoCompleteOnPress = useCallback(
    (place: google.maps.places.Place | null) => {
      dispatch(
        addStartPoint({
          id: crypto.randomUUID(),
          type: "StartPoint",
          name: t("screens.plan.startPoint") ?? "",
          location: {
            latitude: place?.location?.lat() ?? 0,
            longitude: place?.location?.lng() ?? 0,
            address: place?.addressComponents?.[0]?.shortText ?? "",
          },
          dayIndex: dayIndex,
        }),
      );
      dispatch(setOverlayScreenAction());
    },
    [dayIndex, dispatch, t],
  );

  const handleCurrentLocationPress = useCallback(async () => {
    const location = await getUserLocation();
    if (!location) {
      return;
    }

    dispatch(
      addStartPoint({
        id: crypto.randomUUID(),
        type: "StartPoint",
        name: t("screens.plan.startPoint") ?? "",
        location: {
          latitude: location.coordinates.latitude,
          longitude: location.coordinates.longitude,
        },
        dayIndex: dayIndex,
      }),
    );
    dispatch(setOverlayScreenAction());
  }, [getUserLocation, dispatch, t, dayIndex]);

  return (
    <div className="starting-point-overlay-component">
      <AddressAutoComplete onPlaceSelect={handleAutoCompleteOnPress} />

      <div className="current-location-container">
        <LinkButton
          type="default"
          size="medium"
          color={colors.primary60}
          iconPrefix={<ExternalIcon size={16} svgName="current-location" />}
          onPress={handleCurrentLocationPress}>
          {t("screens.plan.useCurrentLocation") ?? ""}
        </LinkButton>
      </div>
    </div>
  );
};

const StartingPointHeader: FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <Text variant="fontHeadingMedium">
      {t("screens.plan.addStartingPointHeader") ?? ""}
    </Text>
  );
};

export { StartingPointHeader, StartingPoint };
